import React, { useMemo, useState } from 'react'
import { api } from '../api'
import {
  Category,
  CURRENCY_LABELS,
  SalesChannel,
  SalesChannelParameter,
  SalesChannelParameterCategoryValue,
  SalesChannelParameterType,
  SUPPORTED_CURRENCIES,
} from '../types'
import { SearchableSelect } from './SearchableSelect'

interface Props {
  salesChannels: SalesChannel[]
  setSalesChannels?: React.Dispatch<React.SetStateAction<SalesChannel[]>>
  categories?: Category[]
  onRefresh: () => Promise<void> | void
}

interface ParameterModalState {
  isOpen: boolean
  editIndex: number | null // null when adding new, number when editing existing in list
  paramType: SalesChannelParameterType
  label: string
  conditionType: 'none' | 'category'
  valueInput: string
  categoryValues: SalesChannelParameterCategoryValue[]
  error: string | null
}

interface CategoryFilterModalState {
  isOpen: boolean
  editIndex: number | null
  categoryId: string
  valueInput: string
  error: string | null
}

interface SalesChannelParametersEditorProps {
  channelId?: string | null
  parameters: SalesChannelParameter[]
  categories?: Category[]
  onChangeParameters: (params: SalesChannelParameter[]) => void
  resetMarginParamIds: string[]
  onToggleResetMarginParam: (paramId: string, checked: boolean) => void
}

function SalesChannelParametersEditor({
  channelId,
  parameters,
  categories = [],
  onChangeParameters,
  resetMarginParamIds,
  onToggleResetMarginParam,
}: SalesChannelParametersEditorProps) {
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  // Child modal state for adding/editing a parameter
  const [paramModal, setParamModal] = useState<ParameterModalState>({
    isOpen: false,
    editIndex: null,
    paramType: 'multiplier',
    label: '',
    conditionType: 'none',
    valueInput: '',
    categoryValues: [],
    error: null,
  })

  // Child modal state for adding/editing a category filter
  const [catFilterModal, setCatFilterModal] = useState<CategoryFilterModalState>({
    isOpen: false,
    editIndex: null,
    categoryId: '',
    valueInput: '',
    error: null,
  })

  // Check if channel already has a profit margin
  const hasProfitMargin = parameters.some(
    (p, idx) => p.parameter_type === 'profit_margin' && idx !== paramModal.editIndex,
  )

  const handleOpenAddParam = () => {
    setParamModal({
      isOpen: true,
      editIndex: null,
      paramType: 'multiplier',
      label: '',
      conditionType: 'none',
      valueInput: '',
      categoryValues: [],
      error: null,
    })
  }

  const handleOpenEditParam = (index: number) => {
    const p = parameters[index]
    const displayVal =
      p.value !== null && p.value !== undefined
        ? p.parameter_type === 'profit_margin'
          ? String(Number(p.value) * 100)
          : String(p.value)
        : ''

    setParamModal({
      isOpen: true,
      editIndex: index,
      paramType: p.parameter_type,
      label: p.label,
      conditionType: p.condition_type || 'none',
      valueInput: displayVal,
      categoryValues: p.category_values ? [...p.category_values] : [],
      error: null,
    })
  }

  const handleCloseParamModal = () => {
    setParamModal((prev) => ({ ...prev, isOpen: false }))
  }

  // Category filter handlers
  const handleOpenAddCategoryFilter = () => {
    setCatFilterModal({
      isOpen: true,
      editIndex: null,
      categoryId: '',
      valueInput: '',
      error: null,
    })
  }

  const handleOpenEditCategoryFilter = (catIndex: number) => {
    const item = paramModal.categoryValues[catIndex]
    const displayVal =
      paramModal.paramType === 'profit_margin'
        ? String(Number(item.value) * 100)
        : String(item.value)

    setCatFilterModal({
      isOpen: true,
      editIndex: catIndex,
      categoryId: item.category_id,
      valueInput: displayVal,
      error: null,
    })
  }

  const handleSaveCategoryFilter = (e?: React.FormEvent | React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (!catFilterModal.categoryId) {
      setCatFilterModal((prev) => ({ ...prev, error: 'Lütfen bir kategori seçin.' }))
      return
    }

    const numVal = parseFloat(catFilterModal.valueInput)
    if (isNaN(numVal)) {
      setCatFilterModal((prev) => ({ ...prev, error: 'Lütfen geçerli bir sayısal değer girin.' }))
      return
    }

    let storedValue: number
    if (paramModal.paramType === 'multiplier') {
      if (numVal <= 0) {
        setCatFilterModal((prev) => ({ ...prev, error: 'Çarpan değeri 0 dan büyük olmalıdır (örn: 1.20).' }))
        return
      }
      storedValue = numVal
    } else {
      if (numVal < 0 || numVal >= 100) {
        setCatFilterModal((prev) => ({ ...prev, error: 'Kâr marjı yüzdesi 0 ile 100 arasında olmalıdır (örn: %20 için 20).' }))
        return
      }
      storedValue = numVal / 100
    }

    // Check duplicate category
    const isDuplicate = paramModal.categoryValues.some(
      (cv, idx) => cv.category_id === catFilterModal.categoryId && idx !== catFilterModal.editIndex,
    )
    if (isDuplicate) {
      setCatFilterModal((prev) => ({ ...prev, error: 'Bu kategori zaten filtre listesinde mevcut.' }))
      return
    }

    const matchedCat = categories.find((c) => c.id === catFilterModal.categoryId)
    const catName = matchedCat ? matchedCat.name : ''

    const updatedList = [...paramModal.categoryValues]
    if (catFilterModal.editIndex !== null) {
      updatedList[catFilterModal.editIndex] = {
        ...updatedList[catFilterModal.editIndex],
        category_id: catFilterModal.categoryId,
        category_name: catName,
        value: storedValue,
      }
    } else {
      updatedList.push({
        category_id: catFilterModal.categoryId,
        category_name: catName,
        value: storedValue,
      })
    }

    setParamModal((prev) => ({ ...prev, categoryValues: updatedList, error: null }))
    setCatFilterModal((prev) => ({ ...prev, isOpen: false }))
  }

  const handleDeleteCategoryFilter = (catIndex: number) => {
    setParamModal((prev) => ({
      ...prev,
      categoryValues: prev.categoryValues.filter((_, idx) => idx !== catIndex),
    }))
  }

  // Save main parameter
  const handleSaveParam = (e?: React.FormEvent | React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    const trimmedLabel = paramModal.label.trim()
    if (!trimmedLabel) {
      setParamModal((prev) => ({ ...prev, error: 'Lütfen bir parametre başlığı girin.' }))
      return
    }

    let storedValue: number | null = null

    if (paramModal.conditionType === 'none') {
      const numVal = parseFloat(paramModal.valueInput)
      if (isNaN(numVal)) {
        setParamModal((prev) => ({ ...prev, error: 'Lütfen geçerli bir sayısal değer girin.' }))
        return
      }

      if (paramModal.paramType === 'multiplier') {
        if (numVal <= 0) {
          setParamModal((prev) => ({ ...prev, error: 'Çarpan değeri 0 dan büyük olmalıdır (örn: 1.15).' }))
          return
        }
        storedValue = numVal
      } else {
        if (hasProfitMargin) {
          setParamModal((prev) => ({
            ...prev,
            error: 'Bir satış kanalında en fazla 1 adet kâr marjı parametresi bulunabilir.',
          }))
          return
        }
        if (numVal < 0 || numVal >= 100) {
          setParamModal((prev) => ({
            ...prev,
            error: 'Kâr marjı yüzdesi 0 ile 100 arasında olmalıdır (örn: %30 için 30).',
          }))
          return
        }
        storedValue = numVal / 100
      }
    } else {
      // Category based condition
      if (paramModal.categoryValues.length === 0) {
        setParamModal((prev) => ({
          ...prev,
          error: 'Kategori bazlı parametre için en az 1 adet kategori filtresi eklenmelidir.',
        }))
        return
      }
      if (paramModal.paramType === 'profit_margin' && hasProfitMargin) {
        setParamModal((prev) => ({
          ...prev,
          error: 'Bir satış kanalında en fazla 1 adet kâr marjı parametresi bulunabilir.',
        }))
        return
      }
      storedValue = null
    }

    if (paramModal.editIndex !== null) {
      // Updating existing parameter in list
      const updated = [...parameters]
      updated[paramModal.editIndex] = {
        ...updated[paramModal.editIndex],
        parameter_type: paramModal.paramType,
        label: trimmedLabel,
        condition_type: paramModal.conditionType,
        value: storedValue,
        category_values: paramModal.conditionType === 'category' ? paramModal.categoryValues : [],
      }
      const multipliers = updated.filter((p) => p.parameter_type === 'multiplier')
      const margin = updated.find((p) => p.parameter_type === 'profit_margin')
      const finalParams = margin ? [...multipliers, margin] : multipliers
      onChangeParameters(finalParams.map((p, idx) => ({ ...p, order_index: idx })))
    } else {
      // Adding new parameter
      const newParam: SalesChannelParameter = {
        parameter_type: paramModal.paramType,
        label: trimmedLabel,
        condition_type: paramModal.conditionType,
        value: storedValue,
        category_values: paramModal.conditionType === 'category' ? paramModal.categoryValues : [],
        order_index: parameters.length,
      }
      if (paramModal.paramType === 'multiplier') {
        const profitIndex = parameters.findIndex((p) => p.parameter_type === 'profit_margin')
        if (profitIndex !== -1) {
          const updated = [...parameters]
          updated.splice(profitIndex, 0, newParam)
          onChangeParameters(updated.map((p, idx) => ({ ...p, order_index: idx })))
        } else {
          onChangeParameters([...parameters, newParam].map((p, idx) => ({ ...p, order_index: idx })))
        }
      } else {
        onChangeParameters([...parameters, newParam].map((p, idx) => ({ ...p, order_index: idx })))
      }
    }

    handleCloseParamModal()
  }

  const handleDeleteParam = (index: number) => {
    const target = parameters[index]
    if (target.id) {
      onToggleResetMarginParam(target.id, false)
    }
    const updated = parameters.filter((_, idx) => idx !== index).map((p, idx) => ({ ...p, order_index: idx }))
    onChangeParameters(updated)
  }

  const handleDragStart = (index: number) => {
    if (parameters[index].parameter_type === 'profit_margin') return
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (targetIndex: number) => {
    if (draggedIndex === null || draggedIndex === targetIndex) return
    const updated = [...parameters]
    const [moved] = updated.splice(draggedIndex, 1)
    updated.splice(targetIndex, 0, moved)

    const multipliers = updated.filter((p) => p.parameter_type === 'multiplier')
    const margin = updated.find((p) => p.parameter_type === 'profit_margin')
    const finalParams = margin ? [...multipliers, margin] : multipliers
    onChangeParameters(finalParams.map((p, idx) => ({ ...p, order_index: idx })))
    setDraggedIndex(null)
  }

  const handleMoveParam = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= parameters.length) return
    const updated = [...parameters]
    const [moved] = updated.splice(index, 1)
    updated.splice(targetIndex, 0, moved)

    const multipliers = updated.filter((p) => p.parameter_type === 'multiplier')
    const margin = updated.find((p) => p.parameter_type === 'profit_margin')
    const finalParams = margin ? [...multipliers, margin] : multipliers
    onChangeParameters(finalParams.map((p, idx) => ({ ...p, order_index: idx })))
  }

  return (
    <div
      style={{
        marginTop: '1.25rem',
        padding: '1rem',
        backgroundColor: 'var(--bg-secondary, #f9fafb)',
        borderRadius: '8px',
        border: '1px solid var(--border-color, #e5e7eb)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>
            Fiyat Hesaplama Parametreleri
          </h4>
          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
            Parametreler yukarıdan aşağıya sırayla uygulanır. Sıralamayı değiştirmek için satırları sürükleyebilirsiniz.
          </span>
        </div>
      </div>

      {parameters.length === 0 ? (
        <div
          style={{
            padding: '1.5rem',
            textAlign: 'center',
            backgroundColor: 'var(--bg-primary, #ffffff)',
            borderRadius: '6px',
            border: '1px dashed var(--border-color, #d1d5db)',
            marginBottom: '1rem',
          }}
        >
          <p className="text-muted" style={{ margin: 0, fontSize: '0.875rem' }}>
            Henüz parametre eklenmedi. Satış kanalına çarpan veya kâr marjı eklemek için aşağıdaki butonu kullanın.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', marginBottom: '1rem' }}>
          {parameters.map((param, index) => {
            const isProfit = param.parameter_type === 'profit_margin'
            const isDragging = draggedIndex === index
            const isCategoryBased = param.condition_type === 'category'
            const catValues = param.category_values || []

            return (
              <div
                key={param.id || `param-${index}`}
                draggable={!isProfit}
                onDragStart={() => handleDragStart(index)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}
                style={{
                  padding: '0.75rem 0.875rem',
                  backgroundColor: 'var(--bg-primary, #ffffff)',
                  border: '1px solid var(--border-color, #e5e7eb)',
                  borderRadius: '6px',
                  opacity: isDragging ? 0.4 : 1,
                  boxShadow: isDragging ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                  {/* Left: Drag handle, order badge, label */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flex: 1, minWidth: 0 }}>
                    {!isProfit ? (
                      <div
                        style={{
                          cursor: 'grab',
                          color: 'var(--text-muted, #9ca3af)',
                          userSelect: 'none',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="Sıralamayı değiştirmek için sürükleyin"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="9" cy="6" r="1.8" />
                          <circle cx="15" cy="6" r="1.8" />
                          <circle cx="9" cy="12" r="1.8" />
                          <circle cx="15" cy="12" r="1.8" />
                          <circle cx="9" cy="18" r="1.8" />
                          <circle cx="15" cy="18" r="1.8" />
                        </svg>
                      </div>
                    ) : (
                      <div
                        style={{
                          color: 'var(--text-muted, #9ca3af)',
                          userSelect: 'none',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="Kâr marjı her zaman listenin en sonunda uygulanır"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </div>
                    )}

                    <span
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--bg-secondary, #f3f4f6)',
                        color: 'var(--text-color, #374151)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </span>

                    <span className={`badge ${isProfit ? 'badge-profit' : 'badge-info'}`} style={{ fontSize: '0.75rem', flexShrink: 0 }}>
                      {isProfit ? 'Kâr Marjı' : 'Çarpan'}
                    </span>

                    <strong style={{ fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {param.label}
                    </strong>

                    {isCategoryBased && (
                      <span className="badge badge-warning" style={{ fontSize: '0.7rem', flexShrink: 0 }}>
                        Kategori Bazlı ({catValues.length})
                      </span>
                    )}
                  </div>

                  {/* Center: Value preview */}
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', textAlign: 'right' }} className="tabular-nums">
                    {isCategoryBased ? (
                      <span className="text-muted" style={{ fontSize: '0.8rem', fontWeight: 500 }}>
                        {catValues.map((cv) => `${cv.category_name || 'Kategori'}: ${isProfit ? `+%${Number(cv.value) * 100}` : `${cv.value}x`}`).join(', ')}
                      </span>
                    ) : isProfit ? (
                      `+%${(Number(param.value) * 100).toFixed(1)}`
                    ) : (
                      `${Number(param.value).toFixed(2)}x`
                    )}
                  </div>

                  {/* Right: Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}
                      disabled={isProfit || index === 0}
                      onClick={() => handleMoveParam(index, 'up')}
                      title={isProfit ? 'Kâr marjı her zaman listenin en sonunda yer alır' : 'Yukarı taşı'}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}
                      disabled={isProfit || index >= (hasProfitMargin ? parameters.length - 2 : parameters.length - 1)}
                      onClick={() => handleMoveParam(index, 'down')}
                      title={isProfit ? 'Kâr marjı her zaman listenin en sonunda yer alır' : 'Aşağı taşı'}
                    >
                      ▼
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={() => handleOpenEditParam(index)}
                    >
                      Düzenle
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={() => handleDeleteParam(index)}
                    >
                      Sil
                    </button>
                  </div>
                </div>

                {/* Fine-grained reset checkbox */}
                {channelId && isProfit && param.id && (
                  <div
                    style={{
                      marginTop: '0.625rem',
                      paddingTop: '0.5rem',
                      borderTop: '1px solid var(--border-color, #f3f4f6)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        cursor: 'pointer',
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                        lineHeight: 1.4,
                        color: 'var(--text-secondary, #4b5563)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={resetMarginParamIds.includes(param.id)}
                        onChange={(e) => onToggleResetMarginParam(param.id!, e.target.checked)}
                        style={{ marginTop: '2px', width: '15px', height: '15px', flexShrink: 0 }}
                      />
                      <span>
                        Bu kutucuk işaretlendiğinde fiyat listelerinde özel {param.label} belirtilmiş ürünler dahil tüm ürünlerin {param.label} değeri güncellenecektir.
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <button
        type="button"
        className="btn btn-secondary"
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
        onClick={handleOpenAddParam}
      >
        <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>+</span>
        <span>Yeni Parametre Ekle</span>
      </button>

      {/* Child Modal: Add / Edit Parameter */}
      {paramModal.isOpen && (
        <div className="modal-backdrop" style={{ zIndex: 1200 }}>
          <div className="modal-card" style={{ maxWidth: '520px', width: '92%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3>{paramModal.editIndex !== null ? 'Parametreyi Düzenle' : 'Yeni Parametre Ekle'}</h3>

            {paramModal.error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{paramModal.error}</div>}

            <div>
              <div className="form-group">
                <label>Parametre Türü *</label>
                <select
                  value={paramModal.paramType}
                  onChange={(e) =>
                    setParamModal((prev) => ({
                      ...prev,
                      paramType: e.target.value as SalesChannelParameterType,
                      error: null,
                    }))
                  }
                >
                  <option value="multiplier">Katsayı</option>
                  <option value="profit_margin" disabled={hasProfitMargin}>
                    Kâr Marjı {hasProfitMargin ? '(Kanalda Zaten Tanımlı - En Fazla 1 Adet)' : ''}
                  </option>
                </select>
                <span className="helper-text">
                  {paramModal.paramType === 'multiplier'
                    ? 'Çarpanlar genel gider veya işletme payı için kullanılır ve fiyat listesinde ürün bazında değiştirilemez.'
                    : 'Kâr marjları fiyat listelerinde sütun olarak görüntülenir ve ürün bazında özel olarak değiştirilebilir.'}
                </span>
              </div>

              <div className="form-group">
                <label>Parametre Başlığı *</label>
                <input
                  required
                  placeholder={
                    paramModal.paramType === 'multiplier'
                      ? 'Örn: Genel Gider Katsayısı, Mağaza İşçilik Gideri'
                      : 'Örn: Perakende Satış Kar Marjı, Toptan Satış Karı'
                  }
                  value={paramModal.label}
                  onChange={(e) => setParamModal((prev) => ({ ...prev, label: e.target.value, error: null }))}
                />
              </div>

              {/* Koşul Selection: Hiçbiri vs Kategori */}
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600 }}>Koşul:</label>
                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 500 }}>
                    <input
                      type="radio"
                      name="conditionType"
                      value="none"
                      checked={paramModal.conditionType === 'none'}
                      onChange={() => setParamModal((prev) => ({ ...prev, conditionType: 'none', error: null }))}
                    />
                    <span>Hiçbiri</span>
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 500 }}>
                    <input
                      type="radio"
                      name="conditionType"
                      value="category"
                      checked={paramModal.conditionType === 'category'}
                      onChange={() => setParamModal((prev) => ({ ...prev, conditionType: 'category', error: null }))}
                    />
                    <span>Kategori</span>
                  </label>
                </div>
              </div>

              {/* If "Hiçbiri", display direct value input */}
              {paramModal.conditionType === 'none' ? (
                <div className="form-group">
                  <label>
                    {paramModal.paramType === 'multiplier' ? 'Çarpan Değeri *' : 'Kâr Marjı Yüzdesi (%) *'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder={paramModal.paramType === 'multiplier' ? '1.15' : '30'}
                    value={paramModal.valueInput}
                    onChange={(e) => setParamModal((prev) => ({ ...prev, valueInput: e.target.value, error: null }))}
                  />
                  <span className="helper-text">
                    {paramModal.paramType === 'multiplier'
                      ? 'Maliyet üzerine doğrudan çarpılacak katsayı (Örn: 1.15 veya 1.50).'
                      : 'Yüzde cinsinden kâr oranı (Örn: %30 kâr marjı için 30 girin).'}
                  </span>
                </div>
              ) : (
                /* If "Kategori", display vertical list of category filters with + button */
                <div
                  style={{
                    padding: '0.875rem',
                    backgroundColor: 'var(--bg-secondary, #f9fafb)',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color, #e5e7eb)',
                    marginBottom: '1rem',
                  }}
                >
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                    Kategori Filtreleri ve Değerleri
                  </label>
                  {paramModal.categoryValues.length === 0 ? (
                    <div
                      style={{
                        padding: '1rem',
                        textAlign: 'center',
                        backgroundColor: 'var(--bg-primary, #ffffff)',
                        borderRadius: '6px',
                        border: '1px dashed var(--border-color, #d1d5db)',
                        marginBottom: '0.75rem',
                      }}
                    >
                      <span className="text-muted" style={{ fontSize: '0.8125rem' }}>
                        Henüz kategori filtresi eklenmedi. Kategoriye özel değer atamak için aşağıdaki butona tıklayın.
                      </span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      {paramModal.categoryValues.map((cv, cIdx) => (
                        <div
                          key={cv.category_id || `cv-${cIdx}`}
                          style={{
                            padding: '0.625rem 0.75rem',
                            backgroundColor: 'var(--bg-primary, #ffffff)',
                            border: '1px solid var(--border-color, #e5e7eb)',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>
                            <span className="badge badge-info" style={{ marginRight: '6px', fontSize: '0.75rem' }}>
                              Kategori
                            </span>
                            <strong style={{ fontSize: '0.875rem' }}>
                              {cv.category_name || categories.find((c) => c.id === cv.category_id)?.name || 'Kategori'}
                            </strong>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontWeight: 700, fontSize: '0.9rem' }} className="tabular-nums">
                              {paramModal.paramType === 'multiplier'
                                ? `${Number(cv.value).toFixed(2)}x`
                                : `+%${(Number(cv.value) * 100).toFixed(1)}`}
                            </span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}
                                onClick={() => handleOpenEditCategoryFilter(cIdx)}
                              >
                                Düzenle
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}
                                onClick={() => handleDeleteCategoryFilter(cIdx)}
                              >
                                Sil
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    onClick={handleOpenAddCategoryFilter}
                  >
                    <span style={{ fontSize: '1rem', lineHeight: 1 }}>+</span>
                    <span>Kategori Filtresi Ekle</span>
                  </button>
                </div>
              )}

              <div className="modal-actions" style={{ marginTop: '1.25rem' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCloseParamModal}>
                  Vazgeç
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSaveParam}>
                  {paramModal.editIndex !== null ? 'Tamam' : 'Parametreyi Ekle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Child Modal: Kategori Filtresi Ekle / Düzenle */}
      {catFilterModal.isOpen && (
        <div className="modal-backdrop" style={{ zIndex: 1300 }}>
          <div className="modal-card" style={{ maxWidth: '440px', width: '90%' }}>
            <h3>{catFilterModal.editIndex !== null ? 'Kategori Filtresini Düzenle' : 'Kategori Filtresi Ekle'}</h3>

            {catFilterModal.error && (
              <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                {catFilterModal.error}
              </div>
            )}

            <div className="form-group">
              <label>Kategori Seçin *</label>
              <SearchableSelect
                options={categories.map((c) => ({
                  id: c.id,
                  label: c.name,
                }))}
                value={catFilterModal.categoryId}
                onChange={(catId) => setCatFilterModal((prev) => ({ ...prev, categoryId: catId, error: null }))}
                placeholder="Kategori arayın veya seçin…"
                required
                emptyMessage="Kategori bulunamadı."
              />
            </div>

            <div className="form-group">
              <label>
                {paramModal.paramType === 'multiplier' ? 'Bu Kategori İçin Çarpan Değeri *' : 'Bu Kategori İçin Kâr Marjı Yüzdesi (%) *'}
              </label>
              <input
                type="number"
                step="0.01"
                required
                placeholder={paramModal.paramType === 'multiplier' ? '1.20' : '20'}
                value={catFilterModal.valueInput}
                onChange={(e) => setCatFilterModal((prev) => ({ ...prev, valueInput: e.target.value, error: null }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSaveCategoryFilter()
                  }
                }}
              />
              <span className="helper-text">
                {paramModal.paramType === 'multiplier'
                  ? 'Bu kategorideki ürünler için uygulanacak katsayı (örn: 1.20).'
                  : 'Bu kategorideki ürünler için uygulanacak kâr marjı yüzdesi (örn: 20).'}
              </span>
            </div>

            <div className="modal-actions" style={{ marginTop: '1.25rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCatFilterModal((prev) => ({ ...prev, isOpen: false }))}
              >
                Vazgeç
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveCategoryFilter}>
                {catFilterModal.editIndex !== null ? 'Güncelle' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function getAvailableBaseChannels(allChannels: SalesChannel[], currentChannelId?: string): SalesChannel[] {
  if (!currentChannelId) return allChannels

  const childrenMap = new Map<string, string[]>()
  for (const ch of allChannels) {
    if (ch.base_sales_channel_id) {
      const arr = childrenMap.get(ch.base_sales_channel_id) || []
      arr.push(ch.id)
      childrenMap.set(ch.base_sales_channel_id, arr)
    }
  }

  const descendants = new Set<string>([currentChannelId])
  const queue = [currentChannelId]
  while (queue.length > 0) {
    const cid = queue.shift()!
    const children = childrenMap.get(cid) || []
    for (const childId of children) {
      if (!descendants.has(childId)) {
        descendants.add(childId)
        queue.push(childId)
      }
    }
  }

  return allChannels.filter((ch) => !descendants.has(ch.id))
}

export function SalesChannelsView({
  salesChannels,
  setSalesChannels,
  categories = [],
  onRefresh,
}: Props) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingChannel, setEditingChannel] = useState<SalesChannel | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [localHiddenIds, setLocalHiddenIds] = useState<Set<string>>(new Set())

  // Main Form state for Add/Edit Sales Channel modal
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [email, setEmail] = useState('')
  const [baseType, setBaseType] = useState<'cost' | 'channel'>('cost')
  const [baseSalesChannelId, setBaseSalesChannelId] = useState<string>('')
  const [parameters, setParameters] = useState<SalesChannelParameter[]>([])
  const [resetMarginParamIds, setResetMarginParamIds] = useState<string[]>([])
  const [preferredCurrency, setPreferredCurrency] = useState<string>('TL')
  const [exchangeRateOverrides, setExchangeRateOverrides] = useState<Record<string, string>>({})
  const [includeKdv, setIncludeKdv] = useState<boolean>(true)

  // TCMB Exchange Rates state
  const [tcmbRates, setTcmbRates] = useState<Record<string, number>>({})
  const [tcmbRatesUpdatedAt, setTcmbRatesUpdatedAt] = useState<string | null>(null)

  // State for dedicated View / Edit Parameters modal
  const [viewingParamsChannel, setViewingParamsChannel] = useState<SalesChannel | null>(null)
  const [channelParams, setChannelParams] = useState<SalesChannelParameter[]>([])
  const [channelResetMarginIds, setChannelResetMarginIds] = useState<string[]>([])
  const [channelParamsError, setChannelParamsError] = useState<string | null>(null)
  const [channelParamsSaving, setChannelParamsSaving] = useState(false)

  const availableBaseChannels = useMemo(
    () => getAvailableBaseChannels(salesChannels, editingChannel?.id),
    [salesChannels, editingChannel],
  )

  const loadTcmbRates = async () => {
    try {
      const res = await api.getCurrencyRates()
      setTcmbRates(res.rates || {})
      setTcmbRatesUpdatedAt(res.updated_at || null)
    } catch (err) {
      console.error('TCMB kurları alınamadı:', err)
    }
  }

  React.useEffect(() => {
    loadTcmbRates()
  }, [])

  const resetForm = () => {
    setName('')
    setCode('')
    setEmail('')
    setBaseType('cost')
    setBaseSalesChannelId('')
    setParameters([])
    setResetMarginParamIds([])
    setPreferredCurrency('TL')
    setExchangeRateOverrides({})
    setIncludeKdv(true)
    setError(null)
  }

  const handleOpenAddModal = () => {
    resetForm()
    loadTcmbRates()
    setShowAddModal(true)
  }

  const handleOpenEditModal = (sc: SalesChannel) => {
    setError(null)
    setEditingChannel(sc)
    setName(sc.name)
    setCode(sc.code || '')
    setEmail(sc.contact_email || '')
    setIncludeKdv(sc.include_kdv !== false)
    if (sc.base_sales_channel_id) {
      setBaseType('channel')
      setBaseSalesChannelId(sc.base_sales_channel_id)
    } else {
      setBaseType('cost')
      setBaseSalesChannelId('')
    }
    const sorted = [...(sc.parameters || [])].sort((a, b) => a.order_index - b.order_index)
    setParameters(sorted.map((p, idx) => ({ ...p, order_index: idx })))
    setResetMarginParamIds([])
    setPreferredCurrency(sc.preferred_currency || 'TL')
    const initialOverrides: Record<string, string> = {}
    if (sc.exchange_rate_overrides) {
      for (const [k, v] of Object.entries(sc.exchange_rate_overrides)) {
        if (v !== null && v !== undefined) {
          initialOverrides[k] = String(v)
        }
      }
    }
    setExchangeRateOverrides(initialOverrides)
    loadTcmbRates()
  }

  const handleOpenParamsModal = (sc: SalesChannel) => {
    setViewingParamsChannel(sc)
    const sorted = [...(sc.parameters || [])].sort((a, b) => a.order_index - b.order_index)
    setChannelParams(sorted.map((p, idx) => ({ ...p, order_index: idx })))
    setChannelResetMarginIds([])
    setChannelParamsError(null)
    setChannelParamsSaving(false)
  }

  const handleToggleResetMarginParam = (paramId: string, checked: boolean) => {
    if (checked) {
      setResetMarginParamIds((prev) => (prev.includes(paramId) ? prev : [...prev, paramId]))
    } else {
      setResetMarginParamIds((prev) => prev.filter((id) => id !== paramId))
    }
  }

  const handleToggleChannelResetMargin = (paramId: string, checked: boolean) => {
    if (checked) {
      setChannelResetMarginIds((prev) => (prev.includes(paramId) ? prev : [...prev, paramId]))
    } else {
      setChannelResetMarginIds((prev) => prev.filter((id) => id !== paramId))
    }
  }

  const handleCreate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    setError(null)
    const cleanName = name.trim()
    if (!cleanName) {
      setError('Lütfen bir satış kanalı adı girin.')
      return
    }
    if (baseType === 'channel' && !baseSalesChannelId) {
      setError('Lütfen temel alınacak bir satış kanalı seçin.')
      return
    }

    const cleanedOverrides: Record<string, number> = {}
    for (const [k, v] of Object.entries(exchangeRateOverrides)) {
      const num = parseFloat(v)
      if (!isNaN(num) && num > 0) {
        cleanedOverrides[k] = num
      }
    }

    try {
      const created = await api.createSalesChannel({
        name: cleanName,
        code: code || null,
        contact_email: email || null,
        base_sales_channel_id: baseType === 'channel' ? baseSalesChannelId : null,
        parameters,
        preferred_currency: preferredCurrency,
        exchange_rate_overrides: cleanedOverrides,
        include_kdv: includeKdv,
      })
      if (setSalesChannels) {
        setSalesChannels((prev) => [created, ...prev])
      } else {
        onRefresh()
      }
      setShowAddModal(false)
      resetForm()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleUpdate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!editingChannel) return
    setError(null)
    const cleanName = name.trim()
    if (!cleanName) {
      setError('Lütfen bir satış kanalı adı girin.')
      return
    }
    if (baseType === 'channel' && !baseSalesChannelId) {
      setError('Lütfen temel alınacak bir satış kanalı seçin.')
      return
    }

    const cleanedOverrides: Record<string, number> = {}
    for (const [k, v] of Object.entries(exchangeRateOverrides)) {
      const num = parseFloat(v)
      if (!isNaN(num) && num > 0) {
        cleanedOverrides[k] = num
      }
    }

    try {
      const updated = await api.updateSalesChannel(editingChannel.id, {
        name: cleanName,
        code: code || null,
        contact_email: email || null,
        base_sales_channel_id: baseType === 'channel' ? baseSalesChannelId : null,
        parameters,
        reset_margin_param_ids: resetMarginParamIds,
        preferred_currency: preferredCurrency,
        exchange_rate_overrides: cleanedOverrides,
        include_kdv: includeKdv,
      })
      setEditingChannel(null)
      resetForm()
      if (setSalesChannels) {
        setSalesChannels((prev) => prev.map((sc) => (sc.id === updated.id ? updated : sc)))
      } else {
        onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleSaveChannelParams = async () => {
    if (!viewingParamsChannel) return
    setChannelParamsSaving(true)
    setChannelParamsError(null)
    try {
      const updated = await api.updateSalesChannel(viewingParamsChannel.id, {
        parameters: channelParams,
        reset_margin_param_ids: channelResetMarginIds,
      })
      setViewingParamsChannel(null)
      if (setSalesChannels) {
        setSalesChannels((prev) => prev.map((sc) => (sc.id === updated.id ? updated : sc)))
      } else {
        onRefresh()
      }
    } catch (err: any) {
      setChannelParamsError(err.message || 'Parametreler kaydedilirken bir hata oluştu.')
    } finally {
      setChannelParamsSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Bu satış kanalını silmek istediğinizden emin misiniz?')) return
    const itemToDelete = salesChannels.find((sc) => sc.id === id)
    const originalIndex = salesChannels.findIndex((sc) => sc.id === id)

    if (setSalesChannels) {
      setSalesChannels((prev) => prev.filter((sc) => sc.id !== id))
    }
    setLocalHiddenIds((prev) => new Set(prev).add(id))

    try {
      await api.deleteSalesChannel(id)
    } catch (err: any) {
      if (setSalesChannels && itemToDelete) {
        setSalesChannels((prev) => {
          if (prev.some((sc) => sc.id === id)) return prev
          const next = [...prev]
          next.splice(originalIndex >= 0 ? originalIndex : 0, 0, itemToDelete)
          return next
        })
      }
      setLocalHiddenIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const displaySalesChannels = salesChannels.filter((sc) => !localHiddenIds.has(sc.id))

  return (
    <div className="view-container">
      <div className="section-header-flex">
        <div>
          <h2>Satış Kanalları</h2>
        </div>
        <button className="btn btn-primary" onClick={handleOpenAddModal}>
          + Yeni Satış Kanalı Ekle
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-card">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '110px' }}>Kanal Kodu</th>
              <th>Satış Kanalı Adı</th>
              <th style={{ width: '150px' }}>Para Birimi</th>
              <th style={{ width: '160px' }}>Temel Satış Kanalı</th>
              <th>İletişim E-posta</th>
              <th style={{ width: '170px' }}>Hesaplama Parametreleri</th>
              <th style={{ width: '130px' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {displaySalesChannels.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center empty-state" style={{ padding: '3rem 1rem' }}>
                  <div style={{ color: 'var(--text-muted, #6b7280)', marginBottom: '0.75rem' }}>
                    Henüz tanımlı bir satış kanalı bulunmamaktadır.
                  </div>
                  <button className="btn btn-sm btn-primary" onClick={handleOpenAddModal}>
                    + İlk Satış Kanalını Ekle
                  </button>
                </td>
              </tr>
            ) : (
              displaySalesChannels.map((sc) => {
                const params = sc.parameters || []
                const baseName = sc.base_sales_channel_name || (sc.base_sales_channel_id ? salesChannels.find((c) => c.id === sc.base_sales_channel_id)?.name : null)

                return (
                  <tr key={sc.id}>
                    <td>
                      <code>{sc.code || '—'}</code>
                    </td>
                    <td>
                      <strong>{sc.name}</strong>
                      {sc.include_kdv === false && (
                        <span className="badge badge-secondary" style={{ marginLeft: '6px', fontSize: '0.75rem' }}>
                          KDV Hariç
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-info" style={{ fontSize: '0.8rem' }}>
                        {CURRENCY_LABELS[sc.preferred_currency || 'TL'] || sc.preferred_currency || 'TL'}
                      </span>
                      {sc.preferred_currency && sc.preferred_currency !== 'TL' && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)', marginTop: '2px' }}>
                          1 {sc.preferred_currency} = {(sc.effective_exchange_rates?.[sc.preferred_currency] ?? tcmbRates[sc.preferred_currency])?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) ?? '—'} ₺
                        </div>
                      )}
                    </td>
                    <td>
                      {baseName ? (
                        <span className="badge badge-info" style={{ fontSize: '0.8rem' }}>
                          {baseName}
                        </span>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                          Maliyet
                        </span>
                      )}
                    </td>
                    <td className="text-muted">{sc.contact_email || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        onClick={() => handleOpenParamsModal(sc)}
                        title="Satış kanalının parametrelerini görüntüle ve düzenle"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        <span>Görüntüle ({params.length})</span>
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleOpenEditModal(sc)}
                        >
                          Düzenle
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(sc.id)}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal: View & Edit Channel Parameters */}
      {viewingParamsChannel && (
        <div className="modal-backdrop">
          <div className="modal-card modal-card-lg" style={{ maxWidth: '780px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
              <div>
                <h3 style={{ margin: 0 }}>{viewingParamsChannel.name} — Hesaplama Parametreleri</h3>
              </div>
              <button
                type="button"
                className="close-button"
                onClick={() => setViewingParamsChannel(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  lineHeight: 1,
                  cursor: 'pointer',
                  color: 'var(--text-muted, #9ca3af)',
                  padding: '0.25rem',
                }}
                title="Kapat"
              >
                &times;
              </button>
            </div>

            {channelParamsError && (
              <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                {channelParamsError}
              </div>
            )}

            <SalesChannelParametersEditor
              channelId={viewingParamsChannel.id}
              parameters={channelParams}
              categories={categories}
              onChangeParameters={setChannelParams}
              resetMarginParamIds={channelResetMarginIds}
              onToggleResetMarginParam={handleToggleChannelResetMargin}
            />

            <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setViewingParamsChannel(null)}
                disabled={channelParamsSaving}
              >
                İptal
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveChannelParams}
                disabled={channelParamsSaving}
              >
                {channelParamsSaving ? 'Kaydediliyor...' : 'Parametreleri Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add / Edit Sales Channel */}
      {(showAddModal || editingChannel) && (
        <div className="modal-backdrop">
          <div className="modal-card modal-card-lg" style={{ maxWidth: '780px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3>{editingChannel ? 'Satış Kanalı Düzenle' : 'Yeni Satış Kanalı Ekle'}</h3>
            <form onSubmit={editingChannel ? handleUpdate : handleCreate}>
              <div className="form-group">
                <label>Satış Kanalı Adı *</label>
                <input
                  required
                  placeholder="Örn: Trendyol, Hepsiburada, Toptan Dağıtım, Mağaza Perakende"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Kanal Kodu</label>
                  <input
                    placeholder="Örn: TRND-01"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>İletişim E-posta</label>
                  <input
                    type="email"
                    placeholder="kanal@ornek.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Tercih Edilen Para Birimi *</label>
                <select
                  value={preferredCurrency}
                  onChange={(e) => setPreferredCurrency(e.target.value)}
                >
                  {SUPPORTED_CURRENCIES.map((curr) => (
                    <option key={curr} value={curr}>
                      {CURRENCY_LABELS[curr] || curr}
                    </option>
                  ))}
                </select>
                <span className="helper-text">
                  Fiyat listelerinde ürün fiyatları bu para biriminde hesaplanır ve görüntülenir.
                </span>
              </div>

              {/* Exchange rate overrides section */}
              <div
                className="form-group"
                style={{
                  padding: '0.875rem',
                  backgroundColor: 'var(--bg-secondary, #f9fafb)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color, #e5e7eb)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <label style={{ margin: 0, fontWeight: 600 }}>
                    Döviz Kurları (TL Karşılığı)
                  </label>
                  {tcmbRatesUpdatedAt && (
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                      TCMB: {new Date(tcmbRatesUpdatedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: 0, marginBottom: '0.75rem' }}>
                  Varsayılan olarak TCMB Döviz Alış kurları otomatik olarak kullanılır. İsteğe bağlı olarak bu satış kanalına özel kur girebilirsiniz.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
                  {SUPPORTED_CURRENCIES.filter((c) => c !== 'TL').map((curr) => {
                    const liveRate = tcmbRates[curr]
                    const overrideVal = exchangeRateOverrides[curr] ?? ''
                    return (
                      <div
                        key={curr}
                        style={{
                          padding: '0.625rem',
                          backgroundColor: 'var(--bg-primary, #ffffff)',
                          border: '1px solid var(--border-color, #e5e7eb)',
                          borderRadius: '6px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                          <strong style={{ fontSize: '0.85rem' }}>{curr} / TL</strong>
                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                            TCMB: {liveRate ? `${Number(liveRate).toFixed(4)} ₺` : 'Yükleniyor…'}
                          </span>
                        </div>
                        <input
                          type="number"
                          step="0.0001"
                          placeholder={liveRate ? `Örn: ${Number(liveRate).toFixed(4)}` : 'Örn: 35.50'}
                          value={overrideVal}
                          onChange={(e) =>
                            setExchangeRateOverrides((prev) => ({
                              ...prev,
                              [curr]: e.target.value,
                            }))
                          }
                          style={{ width: '100%', fontSize: '0.85rem', padding: '0.35rem 0.5rem' }}
                        />
                        <div style={{ fontSize: '0.7rem', color: overrideVal ? '#059669' : 'var(--text-muted, #9ca3af)', marginTop: '3px' }}>
                          {overrideVal ? 'Özel kur aktif' : 'TCMB Döviz Alış kuru geçerli'}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Base Sales Channel Selection */}
              <div
                className="form-group"
                style={{
                  padding: '0.875rem',
                  backgroundColor: 'var(--bg-secondary, #f9fafb)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color, #e5e7eb)',
                }}
              >
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600 }}>
                  Satış fiyatı şunun üzerinden hesaplansın:
                </label>
                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginBottom: '0.625rem' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 500 }}>
                    <input
                      type="radio"
                      name="baseChannelType"
                      value="cost"
                      checked={baseType === 'cost'}
                      onChange={() => {
                        setBaseType('cost')
                        setBaseSalesChannelId('')
                      }}
                    />
                    <span>Maliyet</span>
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 500 }}>
                    <input
                      type="radio"
                      name="baseChannelType"
                      value="channel"
                      checked={baseType === 'channel'}
                      onChange={() => setBaseType('channel')}
                    />
                    <span>Satış Kanalı</span>
                  </label>
                </div>

                {baseType === 'channel' && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <SearchableSelect
                      options={availableBaseChannels.map((sc) => ({
                        id: sc.id,
                        label: sc.name,
                        badge: sc.code || undefined,
                      }))}
                      value={baseSalesChannelId}
                      onChange={(val) => setBaseSalesChannelId(val)}
                      placeholder="Temel alınacak satış kanalını seçin veya arayın…"
                      required={baseType === 'channel'}
                      emptyMessage="Seçilebilecek satış kanalı bulunamadı."
                    />
                    <span className="helper-text" style={{ marginTop: '0.35rem', display: 'block' }}>
                      Bu kanalın ürün satış fiyatları, seçilen temel satış kanalının KDV Hariç Satış Fiyatı üzerinden hesaplanacaktır.
                    </span>
                  </div>
                )}
              </div>

              {/* KDV Enable/Disable Setting */}
              <div
                className="form-group"
                style={{
                  padding: '0.875rem',
                  backgroundColor: 'var(--bg-secondary, #f9fafb)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color, #e5e7eb)',
                }}
              >
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600, margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={includeKdv}
                    onChange={(e) => setIncludeKdv(e.target.checked)}
                  />
                  <span>KDV Hesapla</span>
                </label>
                <span className="helper-text" style={{ display: 'block', marginTop: '6px', fontSize: '0.8rem', color: 'var(--text-muted, #6b7280)' }}>
                  İşaret kaldırıldığında bu satış kanalında KDV hesaplanmaz; Fiyat Listesi tablosunda KDV Dahil Satış Fiyatı ve KDV Oranı kolonları kaldırılır, Final Satış Fiyatı doğrudan KDV Hariç Satış Fiyatı üzerinden hesaplanır.
                </span>
              </div>

              <SalesChannelParametersEditor
                channelId={editingChannel?.id}
                parameters={parameters}
                categories={categories}
                onChangeParameters={setParameters}
                resetMarginParamIds={resetMarginParamIds}
                onToggleResetMarginParam={handleToggleResetMarginParam}
              />

              <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false)
                    setEditingChannel(null)
                  }}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingChannel ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
